/**
 * FrontDeskCheckInPage — Staff taps to verify physical ID at check-in.
 * Route: /front-desk/checkin/:bookingId
 *
 * Shows:
 *  - Booking + guest info (from the booking row — name is already there, typed by guest)
 *  - Online verification badges (age verified / name match level)
 *  - Compliance hint (scan required? pdf fallback? gov portal link?)
 *  - Two buttons: "Verified ✓" or "Mismatch ✗"
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  ShieldCheck, AlertTriangle, Loader2, Check, X, ExternalLink,
  FileText, Printer, User, Globe, Clock, IdCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export default function FrontDeskCheckInPage() {
  const { bookingId } = useParams();
  const [requirements, setRequirements] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [staffId, setStaffId] = useState('');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, bookingRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/compliance/checkin-requirements/${bookingId}`),
        fetch(`${BACKEND_URL}/api/bookings/${bookingId}`),
      ]);
      if (!reqRes.ok) throw new Error('Booking not found');
      setRequirements(await reqRes.json());
      if (bookingRes.ok) setBooking(await bookingRes.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (decision) => {
    if (!staffId.trim()) {
      toast.error('Enter your staff ID first');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/id-verify/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          staff_id: staffId,
          decision,
          notes: notes || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setDone(d);
      toast.success(decision === 'verified' ? 'Check-in verified' : 'Mismatch flagged');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-white flex items-center justify-center" data-testid="fd-loading">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (!requirements) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-white flex items-center justify-center p-6">
        <p className="text-sm text-[#94a3b8]">Booking not found.</p>
      </div>
    );
  }

  const p = requirements.compliance_profile;
  const guestName = booking?.guest_name || requirements.guest_name_on_booking || '—';

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" data-testid="front-desk-checkin">
      <div className="max-w-2xl mx-auto p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748b] mb-1">Front desk · Check-in</p>
        <h1 className="text-xl font-semibold mb-1">{requirements.hotel_name}</h1>
        <p className="text-xs text-[#94a3b8] mb-6">Booking <span className="font-mono">{bookingId}</span></p>

        {/* Guest card */}
        <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-5 mb-4" data-testid="guest-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
              <User className="w-6 h-6 text-cyan-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold truncate" data-testid="guest-name">{guestName}</p>
              {booking && (
                <p className="text-[11px] text-[#94a3b8]">
                  Room {booking.room_number || booking.room_id} · {booking.check_in} → {booking.check_out}
                </p>
              )}
            </div>
          </div>

          {/* Online verification badges */}
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <BadgeRow
              label="Age verified (online)"
              ok={requirements.online_age_verified}
              testid="badge-age"
            />
            <BadgeRow
              label="Name on ID → booking"
              ok={requirements.online_name_match_level === 'match'}
              warn={requirements.online_name_match_level === 'review'}
              note={requirements.online_name_match_level}
              testid="badge-name"
            />
          </div>
        </div>

        {/* Compliance hint */}
        {p.scan_required_every_stay ? (
          <div
            className={`rounded-2xl p-4 mb-4 border ${
              p.paper_fallback
                ? 'bg-amber-500/5 border-amber-500/30'
                : 'bg-red-500/5 border-red-500/30'
            }`}
            data-testid="compliance-hint"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${p.paper_fallback ? 'text-amber-400' : 'text-red-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">
                  {p.reporting_authority} · submission required
                </p>
                <p className="text-[11px] text-[#94a3b8] mt-1">
                  {p.country_name} law requires this check-in be filed with{' '}
                  <strong className="text-white">{p.reporting_authority}</strong>
                  {p.reporting_deadline_hours ? ` within ${p.reporting_deadline_hours}h.` : '.'}
                </p>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {p.paper_fallback && (
                    <a
                      href={`${BACKEND_URL}/api/compliance/pdf-form/${bookingId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-3 py-1.5 rounded-lg"
                      data-testid="open-pdf-form"
                    >
                      <Printer className="w-3 h-3" /> Print check-in form (PDF)
                    </a>
                  )}
                  {p.reporting_portal_url && (
                    <a
                      href={p.reporting_portal_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] bg-white/5 hover:bg-white/10 text-[#94a3b8] hover:text-white px-3 py-1.5 rounded-lg"
                      data-testid="open-gov-portal"
                    >
                      <ExternalLink className="w-3 h-3" /> Open {p.reporting_authority}
                    </a>
                  )}
                  {!p.adapter_shipped && p.reporting_portal_url && (
                    <Badge className="bg-cyan-500/10 text-cyan-400 text-[9px] border-0">
                      AUTO-SUBMIT COMING SOON
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl p-4 mb-4 border bg-emerald-500/5 border-emerald-500/30" data-testid="light-hint">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">No scan required</p>
                <p className="text-[11px] text-[#94a3b8] mt-0.5">
                  Check guest's physical ID vs the booking name + the face in front of you, then tap Verified.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Visual checklist */}
        <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-5 mb-4">
          <p className="text-[10px] uppercase tracking-wider text-[#64748b] mb-3">What to check</p>
          <ul className="space-y-2 text-sm">
            <Check3 text={`Photo on physical ID matches the person in front of you`} />
            <Check3 text={`Name on ID matches booking name: ${guestName}`} />
            <Check3 text={`ID is not expired`} />
            {requirements.guest_is_foreign && (
              <Check3 text={`Note: foreign guest (${requirements.guest_issuing_country}) — file per local rules`} />
            )}
          </ul>
        </div>

        {/* Staff ID + notes */}
        {!done && (
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-5 mb-4 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#64748b]">Your staff ID</label>
              <input
                type="text"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                placeholder="e.g. sarah.frontdesk"
                className="w-full mt-1 bg-[#080c16] border border-[#1a2235] rounded-lg px-3 py-2 text-sm focus:border-cyan-500 outline-none"
                data-testid="staff-id-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#64748b]">Notes (optional)</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything unusual?"
                className="w-full mt-1 bg-[#080c16] border border-[#1a2235] rounded-lg px-3 py-2 text-sm focus:border-cyan-500 outline-none"
                data-testid="notes-input"
              />
            </div>
          </div>
        )}

        {/* Decision buttons */}
        {!done ? (
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={() => decide('verified')}
              disabled={submitting || !staffId.trim()}
              className="bg-emerald-500 hover:bg-emerald-500/90 text-black h-12 text-base font-semibold"
              data-testid="verify-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Check className="w-5 h-5 mr-2" /> Verified</>
              )}
            </Button>
            <Button
              onClick={() => decide('mismatch')}
              disabled={submitting || !staffId.trim()}
              variant="outline"
              className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-12 text-base font-semibold"
              data-testid="mismatch-btn"
            >
              <X className="w-5 h-5 mr-2" /> Mismatch
            </Button>
          </div>
        ) : (
          <div
            className={`rounded-2xl p-5 text-center ${
              done.decision === 'verified'
                ? 'bg-emerald-500/10 border border-emerald-500/30'
                : 'bg-red-500/10 border border-red-500/30'
            }`}
            data-testid="done-card"
          >
            <p className="text-sm font-semibold">
              {done.decision === 'verified' ? 'Check-in verified ✓' : 'Mismatch flagged'}
            </p>
            <p className="text-[11px] text-[#94a3b8] mt-1">
              Logged to audit trail at {new Date(done.verified_at).toLocaleTimeString()}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function BadgeRow({ label, ok, warn, note, testid }) {
  const color = ok ? 'text-emerald-400 bg-emerald-500/10' : warn ? 'text-amber-400 bg-amber-500/10' : 'text-red-400 bg-red-500/10';
  return (
    <div className={`rounded-lg px-3 py-2 ${color}`} data-testid={testid}>
      <p className="text-[9px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-sm font-semibold">
        {ok ? '✓ Pass' : warn ? '⚠ Review' : '✗ Missing'}
        {note && <span className="text-[10px] opacity-70 ml-2">({note})</span>}
      </p>
    </div>
  );
}

function Check3({ text }) {
  return (
    <li className="flex items-start gap-2">
      <div className="w-4 h-4 rounded-sm border border-[#94a3b8] mt-0.5 shrink-0" />
      <span className="text-[#cbd5e1]">{text}</span>
    </li>
  );
}
